import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Avatar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CancelIcon,
  ContentCopy as CopyIcon,
  PersonRemove as RemoveIcon,
  PersonAdd as InviteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ContextMember,
  FinancialContext,
  MemberRole,
  SignupInvitation,
  TelegramAdminsStatus,
  contextApi,
  invitationUrl,
} from '../../services/contextApi';
import {
  assignableRoles,
  canChangeRole,
  canManageMembers,
  canRemoveMember,
  findOwnMembership,
  isInvitationExpired,
  isInvitationPending,
} from '../../utils/contextPermissions';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  open: boolean;
  context: FinancialContext | null;
  onClose: () => void;
}

/** The API's refusals of an invitation, in the person's language. */
const INVITE_ERRORS: Array<[string, string]> = [
  ['already a member', 'members.errors.alreadyMember'],
  ['pending invitation', 'members.errors.alreadyInvited'],
  ['permission', 'members.errors.noPermission'],
];


/** What the server says when Telegram does not allow making someone an admin there. */
const TELEGRAM_ADMIN_REASONS = [
  'no_group',
  'not_supergroup',
  'fin_cannot_promote',
  'you_cannot_promote',
  'telegram_unavailable',
  'no_telegram',
  'not_in_group',
  'owner_in_telegram',
  'admin_in_telegram',
  'member_changed',
];

/**
 * One person's row. On a phone the role goes under the name and email,
 * which wrap, instead of beside them, where it covered both; from tablet
 * width up it sits on the right.
 */
const rowSx = {
  // On a phone the row gives its left gutter back to the name and email. The
  // right keeps room for the remove button on every row (it lines the roles
  // up too), so neither the role nor the text ever runs under it.
  item: { pl: { xs: 0, sm: 2 }, pr: 6, '& .MuiListItemSecondaryAction-root': { right: { xs: 12, sm: 16 } } },
  avatar: { alignSelf: 'flex-start', mt: 0.5, minWidth: { xs: 48, sm: 56 } },
  body: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    columnGap: 2,
    rowGap: 1,
  },
  text: { minWidth: 0, my: 0, width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } },
  wrap: { overflowWrap: 'anywhere' },
  // On a phone the role takes the column's width (up to 220px), so even at
  // 320px it ends before the remove button.
  role: { flexShrink: 0, width: { xs: '100%', sm: 170 }, maxWidth: { xs: 220, sm: 'none' } },
} as const;

/** What a remove or cancel button asks about before anything happens. */
type Confirming = { kind: 'member'; member: ContextMember } | { kind: 'signup'; invitation: SignupInvitation };

/** A phone keeps a thin margin around the dialog instead of 32px a side. */
const paperSx = { m: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' }, maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' } } as const;

export const ContextMembersDialog: React.FC<Props> = ({ open, context, onClose }) => {
  const { t } = useTranslation('contexts');
  const { state: authState } = useAuth();

  const [members, setMembers] = useState<ContextMember[]>([]);
  // People with no account yet, invited to sign up: shown to whoever manages members.
  const [signupInvitations, setSignupInvitations] = useState<SignupInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('member');
  const [isInviting, setIsInviting] = useState(false);
  // The link just created, and for whom: cancelling someone else's invitation leaves it.
  const [pendingLink, setPendingLink] = useState<{ email: string; url: string } | null>(null);
  // Removing someone, or cancelling an invitation, waits for a second click:
  // one stray tap on a row's button should not cost a person their access.
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // In a group linked to Telegram: whether each admin is, or can be, an admin there too.
  const [telegramAdmins, setTelegramAdmins] = useState<TelegramAdminsStatus | null>(null);
  // Whose switch is being saved: one request at a time per person, several people at once.
  const [togglingAdmins, setTogglingAdmins] = useState<ReadonlySet<string>>(new Set());
  const togglingRef = useRef<Set<string>>(new Set());
  // The group the dialog shows now: an answer that comes back for another one is dropped.
  const shownContextId = useRef<string | undefined>(undefined);
  shownContextId.current = open ? context?.id : undefined;
  // Each person's name, where focus goes when the switch it was on goes away.
  const nameRefs = useRef(new Map<string, HTMLElement>());
  const [focusNameOf, setFocusNameOf] = useState<string | null>(null);
  const switchId = (memberId: string) => `telegram-admin-${memberId}`;

  // Right as the switch goes, before anything else can move focus.
  useLayoutEffect(() => {
    if (!focusNameOf) return;
    nameRefs.current.get(focusNameOf)?.focus();
    setFocusNameOf(null);
  }, [focusNameOf]);

  const ownMembership = findOwnMembership(members, authState.user?.id);
  const ownRole = ownMembership?.role;

  const load = useCallback(async () => {
    if (!context) return;

    setIsLoading(true);
    setError(null);

    try {
      const list = await contextApi.getMembers(context.id);
      setMembers(list);
      const own = findOwnMembership(list, authState.user?.id);
      const manages = canManageMembers(own?.role);
      const [invitations, admins] = await Promise.all([
        manages ? contextApi.getSignupInvitations(context.id).catch(() => []) : Promise.resolve([]),
        manages && context.telegramChats?.length
          ? contextApi.getTelegramAdmins(context.id).catch(() => null)
          : Promise.resolve(null),
      ]);
      setSignupInvitations(invitations);
      setTelegramAdmins(admins);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  }, [context, authState.user?.id]);

  useEffect(() => {
    if (!open) {
      setPendingLink(null);
      setNotice(null);
      setInviteEmail('');
      setConfirming(null);
      setTelegramAdmins(null);
      togglingRef.current.clear();
      setTogglingAdmins(new Set());
      return;
    }
    load();
  }, [open, load]);

  const invite = async () => {
    if (!context || !inviteEmail.trim()) return;

    setIsInviting(true);
    setError(null);
    setNotice(null);

    try {
      const recipient = inviteEmail.trim();
      const member = await contextApi.invite(context.id, {
        email: recipient,
        role: inviteRole,
      });

      setInviteEmail('');
      // The link is shown either way: it is the only delivery route when email
      // is not configured, and a useful fallback when it is.
      setPendingLink(member.inviteToken ? { email: recipient.toLowerCase(), url: invitationUrl(member.inviteToken) } : null);
      if (member.pendingSignup) {
        // No account with that email: the link creates one and joins the group.
        setNotice(
          member.invitationEmailSent
            ? t('members.signupEmailed', { email: recipient })
            : t('members.signupCreated', { email: recipient }),
        );
      } else {
        setNotice(
          member.invitationEmailSent
            ? t('members.inviteEmailed', { email: recipient })
            : t('members.inviteCreated'),
        );
      }
      await load();
    } catch (err: any) {
      const message: string = err?.response?.data?.message || err.message;
      const known = INVITE_ERRORS.find(([fragment]) => message?.includes(fragment));
      setError(known ? t(known[1]) : message);
    } finally {
      setIsInviting(false);
    }
  };

  const changeRole = async (member: ContextMember, role: MemberRole) => {
    if (!context) return;

    setError(null);
    try {
      await contextApi.updateMemberRole(context.id, member.id, role);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const markToggling = (memberId: string, busy: boolean) => {
    if (busy) togglingRef.current.add(memberId);
    else togglingRef.current.delete(memberId);
    setTogglingAdmins(new Set(togglingRef.current));
  };

  const toggleTelegramAdmin = async (member: ContextMember, enabled: boolean) => {
    // One request at a time per person: the switch stays focusable meanwhile.
    if (!context || togglingRef.current.has(member.id)) return;
    const contextId = context.id;
    const stillShown = () => shownContextId.current === contextId;

    setError(null);
    markToggling(member.id, true);
    try {
      const state = await contextApi.setTelegramAdmin(contextId, member.id, enabled);
      if (!stillShown()) return;
      // Still on the switch (not moved elsewhere while it saved): focus follows it.
      const switchHadFocus = document.activeElement?.id === switchId(member.id);
      setTelegramAdmins((current) =>
        current && { ...current, members: current.members.map((entry) => (entry.memberId === state.memberId ? state : entry)) },
      );
      if (member.role !== 'admin' && !state.on) {
        // An owner's switch goes once off: say it worked, and keep the place in the list.
        setNotice(t('members.telegramAdmin.ownerOff', { name: displayName(member) }));
        if (switchHadFocus) setFocusNameOf(member.id);
      }
    } catch (err: any) {
      if (!stillShown()) return;
      // Telegram changed meanwhile (the person left, Fin lost a right): say why, and show where it stands now.
      const reason: string | undefined = err?.response?.data?.message;
      setError(
        reason && TELEGRAM_ADMIN_REASONS.includes(reason)
          ? t(`members.telegramAdmin.reasons.${reason}`, { name: displayName(member) })
          : t('members.telegramAdmin.failed'),
      );
      const fresh = await contextApi.getTelegramAdmins(contextId).catch(() => null);
      if (!fresh || !stillShown()) return;
      // Rows still being saved keep what they show until their own answer comes back.
      setTelegramAdmins((current) => ({
        ...fresh,
        members: fresh.members.map((entry) =>
          entry.memberId !== member.id && togglingRef.current.has(entry.memberId)
            ? current?.members.find((shown) => shown.memberId === entry.memberId) ?? entry
            : entry,
        ),
      }));
    } finally {
      markToggling(member.id, false);
    }
  };

  /** "Também admin no grupo do Telegram", under an admin's role, for whoever manages members. */
  const telegramAdminControl = (member: ContextMember) => {
    // Listed for every admin, and for an owner while Fin keeps them an admin there.
    const state = telegramAdmins?.members.find((entry) => entry.memberId === member.id);
    if (!telegramAdmins || !state || !canManageMembers(ownRole)) return null;
    // Only an admin can be made one: an owner's switch is gone once it's off.
    if (member.role !== 'admin' && !state.on) return null;
    const reason = telegramAdmins.unavailable ?? state.unavailable;
    const reasonId = `telegram-admin-reason-${member.id}`;
    const busy = togglingAdmins.has(member.id);
    // Taking an admin back needs no rights in Telegram: someone who can't make
    // admins there can still switch one off, while Fin can act in the group.
    const canOnlyTurnOff = state.on && reason !== null && !['fin_cannot_promote', 'telegram_unavailable'].includes(reason);
    return (
      <Box sx={{ mt: 0.5 }}>
        <FormControlLabel
          sx={{ mr: 0, alignItems: 'center' }}
          control={
            <Switch
              size="small"
              // Shows who Fin made an admin there even when nothing can change now.
              checked={state.on}
              disabled={Boolean(reason) && !canOnlyTurnOff}
              onChange={(event) => toggleTelegramAdmin(member, event.target.checked)}
              inputProps={{
                id: switchId(member.id),
                'aria-label': t('members.telegramAdmin.labelFor', { name: displayName(member) }),
                'aria-busy': busy,
                ...(reason ? { 'aria-describedby': reasonId } : {}),
              }}
            />
          }
          label={<Typography variant="body2">{t('members.telegramAdmin.label')}</Typography>}
        />
        {reason && (
          <Typography id={reasonId} variant="caption" color="text.secondary" component="p" sx={{ m: 0, ...rowSx.wrap }}>
            {t(`members.telegramAdmin.reasons.${reason}`, { name: displayName(member) })}
          </Typography>
        )}
      </Box>
    );
  };

  const remove = async (member: ContextMember) => {
    if (!context) return;

    setError(null);
    try {
      await contextApi.removeMember(context.id, member.id);
      await load();
      if (isInvitationPending(member)) {
        // A pending invitation is cancelled: the link just made for it no longer works.
        const email = member.user?.email?.toLowerCase();
        setPendingLink((link) => (email && link?.email === email ? null : link));
        setNotice(t('members.invitationCancelled'));
      } else if (context.telegramChats?.length) {
        // The removal holds in the app, but the person still reads the Telegram group.
        setNotice(t('members.removedTelegram'));
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const cancelInvitation = async (invitation: SignupInvitation) => {
    if (!context) return;

    setError(null);
    try {
      await contextApi.cancelSignupInvitation(context.id, invitation.id);
      setPendingLink((link) => (link?.email === invitation.email ? null : link));
      setNotice(t('members.invitationCancelled'));
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    }
  };

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setNotice(t('members.linkCopied'));
    } catch {
      // Clipboard access can be denied; the link is on screen either way.
      setNotice(t('members.linkCopyFailed'));
    }
  };

  const displayName = (member: ContextMember): string => {
    const user = member.user;
    if (!user) return t('members.unknownUser');
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.email || t('members.unknownUser');
  };

  const confirm = async () => {
    if (!confirming) return;
    setIsConfirming(true);
    try {
      if (confirming.kind === 'member') await remove(confirming.member);
      else await cancelInvitation(confirming.invitation);
    } finally {
      setIsConfirming(false);
      setConfirming(null);
    }
  };

  // A pending invitation is cancelled, not removed: its link stops working.
  const question = (() => {
    if (!confirming) return null;
    if (confirming.kind === 'signup' || isInvitationPending(confirming.member)) {
      const name = confirming.kind === 'signup' ? confirming.invitation.email : displayName(confirming.member);
      return {
        title: t('members.confirm.cancelInvitationTitle', { name }),
        text: t('members.confirm.cancelInvitationText'),
        action: t('members.cancelInvitation'),
      };
    }
    const name = displayName(confirming.member);
    // Two rows can share a name (an app account and a Telegram profile): the
    // question carries the row's chip, so a tap on the wrong one shows.
    const who = confirming.member.user?.isGuest ? `${name} (${t('members.telegramGuest')})` : name;
    return {
      title: t('members.confirm.removeTitle', { name: who }),
      text: t('members.confirm.removeText', { name }),
      action: t('members.confirm.remove'),
    };
  })();
  // The question fades out after it is answered: it keeps its words meanwhile.
  const lastQuestion = useRef(question);
  if (question) lastQuestion.current = question;
  const shown = question ?? lastQuestion.current;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>
        {t('members.title')}
        {context && (
          <Typography variant="body2" color="text.secondary">
            {context.name}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {canManageMembers(ownRole) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('members.inviteTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('members.inviteHint')}
            </Typography>

            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('members.email')}
                  value={inviteEmail}
                  onChange={(event) => setInviteEmail(event.target.value)}
                  size="small"
                  fullWidth
                  type="email"
                />
              </Grid>
              <Grid item xs={7} sm={3}>
                <TextField
                  select
                  label={t('members.role')}
                  value={inviteRole}
                  onChange={(event) => setInviteRole(event.target.value as MemberRole)}
                  size="small"
                  fullWidth
                >
                  {assignableRoles(ownRole).map((role) => (
                    <MenuItem key={role} value={role}>
                      {t(`roles.${role}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={5} sm={3}>
                <Button
                  onClick={invite}
                  variant="contained"
                  size="medium"
                  fullWidth
                  disabled={isInviting || !inviteEmail.trim()}
                  startIcon={<InviteIcon />}
                >
                  {t('members.invite')}
                </Button>
              </Grid>
            </Grid>

            {pendingLink && (
              <Alert
                severity="info"
                sx={{ mt: 2, wordBreak: 'break-all' }}
                action={
                  <IconButton size="small" onClick={() => copy(pendingLink.url)} aria-label={t('members.copyLink')}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Typography variant="caption" display="block">
                  {t('members.shareLink')}
                </Typography>
                {pendingLink.url}
              </Alert>
            )}

            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List dense>
            {members.map((member) => (
              <ListItem
                key={member.id}
                sx={rowSx.item}
                secondaryAction={
                  canRemoveMember(ownRole, member) ? (
                    <Tooltip title={t('members.remove')}>
                      <IconButton
                        edge="end"
                        onClick={() => setConfirming({ kind: 'member', member })}
                        aria-label={t('members.remove')}
                        disabled={togglingAdmins.has(member.id)}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null
                }
              >
                <ListItemAvatar sx={rowSx.avatar}>
                  <Avatar>{displayName(member).charAt(0).toUpperCase()}</Avatar>
                </ListItemAvatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={rowSx.body}>
                  <ListItemText
                    sx={rowSx.text}
                    secondaryTypographyProps={{ sx: rowSx.wrap }}
                    primary={
                      <Box
                        ref={(node: HTMLElement | null) => {
                          if (node) nameRefs.current.set(member.id, node);
                          else nameRefs.current.delete(member.id);
                        }}
                        tabIndex={-1}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ...rowSx.wrap }}
                      >
                        {displayName(member)}
                        {isInvitationPending(member) && (
                          <Chip
                            size="small"
                            label={
                              isInvitationExpired(member)
                                ? t('members.invitationExpired')
                                : t('members.invitationPending')
                            }
                            color={isInvitationExpired(member) ? 'default' : 'warning'}
                            variant="outlined"
                          />
                        )}
                        {/* Tells a Telegram profile apart from the same person's app account until they merge. */}
                        {member.user?.isGuest && <Chip size="small" label={t('members.telegramGuest')} variant="outlined" />}
                      </Box>
                    }
                    secondary={member.user?.email}
                  />

                  <Box sx={rowSx.role}>
                    {canChangeRole(ownRole, member) ? (
                      <TextField
                        select
                        size="small"
                        value={member.role}
                        onChange={(event) => changeRole(member, event.target.value as MemberRole)}
                        // Not while Fin is making this person an admin in Telegram.
                        disabled={togglingAdmins.has(member.id)}
                        fullWidth
                        SelectProps={{ SelectDisplayProps: { 'aria-label': t('members.roleOf', { name: displayName(member) }) } as React.HTMLAttributes<HTMLDivElement> }}
                      >
                        {assignableRoles(ownRole).map((role) => (
                          <MenuItem key={role} value={role}>
                            {t(`roles.${role}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Chip size="small" label={t(`roles.${member.role}`)} />
                    )}
                  </Box>
                </Box>
                {telegramAdminControl(member)}
                </Box>
              </ListItem>
            ))}

            {signupInvitations.map((invitation) => {
              const expired = new Date(invitation.expiresAt) < new Date();
              return (
                <ListItem
                  key={invitation.id}
                  sx={rowSx.item}
                  secondaryAction={
                    <Tooltip title={t('members.cancelInvitation')}>
                      <IconButton
                        edge="end"
                        onClick={() => setConfirming({ kind: 'signup', invitation })}
                        aria-label={t('members.cancelInvitation')}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemAvatar sx={rowSx.avatar}>
                    <Avatar>{invitation.email.charAt(0).toUpperCase()}</Avatar>
                  </ListItemAvatar>
                  <Box sx={rowSx.body}>
                    <ListItemText
                      sx={rowSx.text}
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ...rowSx.wrap }}>
                          {invitation.email}
                          <Chip
                            size="small"
                            label={expired ? t('members.invitationExpired') : t('members.awaitingSignup')}
                            color={expired ? 'default' : 'warning'}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={t('members.signupPendingHint')}
                    />
                    <Box sx={rowSx.role}>
                      <Chip size="small" label={t(`roles.${invitation.role}`)} />
                    </Box>
                  </Box>
                </ListItem>
              );
            })}

            {members.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t('members.empty')}
              </Typography>
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('actions.close')}</Button>
      </DialogActions>

      <Dialog
        open={Boolean(confirming)}
        onClose={() => !isConfirming && setConfirming(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="member-confirm-title"
      >
        <DialogTitle id="member-confirm-title" sx={{ overflowWrap: 'anywhere' }}>
          {shown?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{shown?.text}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={isConfirming} color="inherit">
            {t('members.confirm.keep')}
          </Button>
          <Button onClick={confirm} disabled={isConfirming} variant="contained" color="error">
            {shown?.action}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
